import type { SubscriptionOptions } from '@reduxjs/toolkit/query'

import { logger } from '@nanostores/logger'
import { useStore } from '@nanostores/react'
import { CONSTS } from 'config/objectConst'
import { atom, onMount } from 'nanostores'
import { useEffect } from 'react'

const INACTIVITY_LIMIT =
	CONSTS.MESSAGING.OFFLINE_INTERVAL_IN_MINUTES * 60 * 1_000
const USER_ACTIVITY_EVENTS = [
	'mousemove',
	'mousedown',
	'keydown',
	'touchstart',
	'scroll'
]
const PULLING_OPTIONS: SubscriptionOptions = {
	pollingInterval: CONSTS.MESSAGING.REFRESH_INTERVAL_IN_SECONDS * 1_000,
	refetchOnReconnect: true,
	skipPollingIfUnfocused: true
}

const $isOn = atom(CONSTS.MESSAGING.AUTO_SYNC)

let timeout = 0

const turnOff = () => $isOn.set(false)
function handleInActiveTab() {
	timeout && window.clearTimeout(timeout)
	turnOff()
}

function restart() {
	timeout && window.clearTimeout(timeout)
	$isOn.set(true)

	timeout = window.setTimeout(turnOff, INACTIVITY_LIMIT)
}
const reactOnVisibility = () =>
	document.hidden ? handleInActiveTab() : restart()

function setUpListeners() {
	const controller = new AbortController()

	const abortOption = {
		signal: controller.signal
	}

	for (const event of USER_ACTIVITY_EVENTS) {
		document.addEventListener(event, restart, abortOption)
	}

	document.addEventListener('offline', handleInActiveTab, abortOption)
	document.addEventListener('online', restart, abortOption)
	document.addEventListener('visibilitychange', reactOnVisibility, abortOption)

	restart()

	return () => {
		timeout && window.clearTimeout(timeout)
		controller.abort()
	}
}

function useAutoSync(): SubscriptionOptions {
	const isSynced = useStore($isOn)

	return isSynced ? PULLING_OPTIONS : {}
}

if (CONSTS.MESSAGING.AUTO_SYNC) {
	onMount($isOn, setUpListeners)
}

if (import.meta.env.DEV) {
	logger({
		'💬🔄': $isOn
	})
}

function useReactOnSyncChange(refetch?: (() => void) | false | null) {
	useEffect(() => {
		if (refetch) {
			return $isOn.listen(isOn => isOn && refetch())
		}
	}, [refetch])
}

export { useAutoSync, useReactOnSyncChange }
